import { generateHit } from './hits';
import { safeLocalStorage, safeSessionStorage } from './safeStorage';
import { API_SEND_EVENTS } from '../../shared/common/api/urls';

/**
 * Сбор статистики действий в ЛК: клики по любым кнопкам, ссылкам, пунктам меню,
 * вкладкам, чекбоксам, а также продуктовые события (применение фильтра, выбор
 * метрик и группировок, смена периода и т.д. — через trackEvent).
 *
 * События копятся в очереди и уходят пачкой, чтобы не плодить запросы на каждый клик.
 */

export type AnalyticsEventType = 'click' | 'change' | 'action' | 'navigate';

export interface AnalyticsEvent {
    event_type: AnalyticsEventType;
    element_id?: string | null;
    element_role?: string | null;
    element_text?: string | null;
    element_path?: string | null;
    page_url: string;
    page_path: string;
    hit?: string | null;
    payload?: Record<string, unknown> | null;
    ts: number;
}

const FLUSH_SIZE = 15;          // отправляем, как только накопилось столько событий
const FLUSH_INTERVAL = 5000;    // либо раз в 5 секунд
const QUEUE_LIMIT = 200;        // страховка, если сеть недоступна
const BATCH_LIMIT = 100;        // столько событий за раз принимает бэкенд (UserEvents::MAX_BATCH)
const TEXT_LIMIT = 120;

let queue: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let bound = false;

const isAuthorized = (): boolean => {
    const token = safeLocalStorage.getItem('access') || safeSessionStorage.getItem('access');
    return !!token;
};

const getToken = (): string => {
    return safeLocalStorage.getItem('access') || safeSessionStorage.getItem('access') || '';
};

// ─── Разбор элемента ────────────────────────────────────────────────────────

/** Классы antd и утилитарные, по которым нельзя опознать элемент */
const NOISE_CLASS = /^(ant-|css-|sc-|_|is-|has-)/;

/** id, сгенерированные библиотеками (rc_select_12, :r3:), опознавать бессмысленно */
const GENERATED_ID = /^(rc_|rc-|:r|react-|ant-)/;

/** id контейнеров страницы: под ними лежит всё подряд, для события они бесполезны */
const CONTAINER_ID = /^(app|root|main|content|wrapper|page|layout)$/i;

/** Иконка внутри кнопки — по ней опознаём кнопки без подписи */
const ICON = '.anticon, [role="img"]';

/** Элементы, по которым кликают осмысленно */
const CLICKABLE = 'button, .ant-btn, a, [role="button"], .ant-menu-item, .ant-dropdown-menu-item,'
    + ' .ant-select-item, .ant-tabs-tab, .ant-switch, .ant-checkbox-wrapper, .ant-radio-wrapper, label';

const cleanText = (value: string | null | undefined): string | null => {
    if (!value) return null;

    const text = value.replace(/\s+/g, ' ').trim();

    return text ? text.slice(0, TEXT_LIMIT) : null;
};

/**
 * Осмысленный идентификатор элемента. Приоритет — у явной разметки:
 * data-analytics-id, затем уже имеющийся в проекте data-help-id.
 */
const getElementId = (el: HTMLElement): string | null => {
    const explicit = el.closest<HTMLElement>('[data-analytics-id]');
    if (explicit) return explicit.dataset.analyticsId || null;

    const help = el.closest<HTMLElement>('[data-help-id]');
    if (help) return help.dataset.helpId || null;

    // id ищем не выше ближайшей кнопки: иначе поднимаемся до контейнеров страницы
    // и разные кнопки склеиваются в один идентификатор (#app, #universal_table)
    const stop = el.closest<HTMLElement>(CLICKABLE) || el;
    let node: HTMLElement | null = el;

    while (node) {
        if (node.id && !GENERATED_ID.test(node.id) && !CONTAINER_ID.test(node.id)) return node.id;
        if (node === stop) break;
        node = node.parentElement;
    }

    return null;
};

/** Тип элемента — чтобы группировать события без разбора вёрстки */
const getElementRole = (el: HTMLElement): string => {
    if (el.closest('.ant-menu-item, .ant-dropdown-menu-item, .ant-select-item')) return 'menu';
    if (el.closest('.ant-tabs-tab')) return 'tab';
    if (el.closest('.ant-checkbox-wrapper, input[type="checkbox"]')) return 'checkbox';
    if (el.closest('.ant-radio-wrapper, input[type="radio"]')) return 'radio';
    if (el.closest('.ant-switch')) return 'switch';
    if (el.closest('.ant-select, .ant-picker')) return 'select';
    if (el.closest('.ant-modal-close, .ant-drawer-close')) return 'close';
    if (el.closest('button, .ant-btn, [role="button"]')) return 'button';
    if (el.closest('a')) return 'link';
    if (el.closest('th, .ant-table-thead')) return 'table-header';
    if (el.closest('tr, .ant-table-row')) return 'table-row';
    if (el.closest('input, textarea')) return 'input';
    if (el.closest('svg, .anticon')) return 'icon';

    return 'other';
};

/** Подпись элемента: сначала явные атрибуты, потом видимый текст */
const getElementText = (el: HTMLElement): string | null => {
    const labelled = el.closest<HTMLElement>('[aria-label], [title], [data-analytics-text]');

    if (labelled) {
        const explicit = cleanText(
            labelled.dataset.analyticsText ||
            labelled.getAttribute('aria-label') ||
            labelled.getAttribute('title')
        );
        if (explicit) return explicit;
    }

    const clickable = el.closest<HTMLElement>(CLICKABLE);
    if (clickable) return cleanText(clickable.innerText) || getIconName(clickable);

    // Клик мимо кнопки: берём текст самого элемента, но только если это подпись,
    // а не содержимое целой панели — иначе в статистику попадают простыни текста
    const own = cleanText(el.innerText);

    return (own && own.length <= 60 ? own : null) || getIconName(el);
};

/**
 * Кнопки-иконки без подписи иначе остаются безымянными. У antd имя иконки
 * лежит либо в aria-label, либо в классе вида `anticon-line-chart`.
 */
const getIconName = (el: HTMLElement): string | null => {
    const icon = el.matches(ICON) ? el : el.querySelector<HTMLElement>(ICON);
    if (!icon) return null;

    const label = icon.getAttribute('aria-label');
    if (label) return `иконка: ${label}`;

    const named = Array.from(icon.classList).find((cls) => cls.startsWith('anticon-'));

    return named ? `иконка: ${named.replace('anticon-', '')}` : null;
};

/** CSS-путь — резервный идентификатор для элементов без разметки */
const getElementPath = (el: HTMLElement): string => {
    const parts: string[] = [];
    let node: HTMLElement | null = el;
    let depth = 0;

    while (node && node.tagName !== 'BODY' && depth < 5) {
        let part = node.tagName.toLowerCase();

        const stableClass = Array.from(node.classList).find((cls) => !NOISE_CLASS.test(cls));

        if (stableClass) {
            part += `.${stableClass}`;
        } else if (node.parentElement) {
            // Вёрстка почти вся на классах antd, которые ничего не различают.
            // Без позиции путь вырождается в «div > div > button» у всех кнопок сразу
            part += `:nth-child(${Array.from(node.parentElement.children).indexOf(node) + 1})`;
        }

        parts.unshift(part);
        node = node.parentElement;
        depth += 1;
    }

    return parts.join(' > ').slice(0, 500);
};

// ─── Очередь и отправка ─────────────────────────────────────────────────────

const currentPage = () => ({
    page_url: window.location.href.slice(0, 1000),
    page_path: window.location.pathname.slice(0, 255),
});

const send = (events: AnalyticsEvent[], useKeepAlive: boolean): void => {
    try {
        // keepalive нужен при закрытии вкладки: обычный запрос браузер оборвёт.
        // sendBeacon здесь не годится — он не умеет отправлять заголовок Token.
        fetch(API_SEND_EVENTS, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Token': getToken(),
            },
            body: JSON.stringify({ events }),
            keepalive: useKeepAlive,
        }).catch(() => {
            // сеть недоступна — пачку теряем, повторять смысла нет
        });
    } catch (error) {
        // аналитика не должна ломать работу интерфейса
    }
};

const flush = (useKeepAlive = false): void => {
    if (!queue.length || !isAuthorized()) return;

    const events = queue;
    queue = [];

    // сервер принимает не больше BATCH_LIMIT событий за раз
    for (let i = 0; i < events.length; i += BATCH_LIMIT) {
        send(events.slice(i, i + BATCH_LIMIT), useKeepAlive);
    }
};

const push = (event: AnalyticsEvent): void => {
    if (!isAuthorized()) return;

    queue.push(event);

    if (queue.length >= FLUSH_SIZE) {
        flush();
    } else if (queue.length > QUEUE_LIMIT) {
        queue = queue.slice(-QUEUE_LIMIT);
    }
};

// ─── Публичный API ──────────────────────────────────────────────────────────

/**
 * Продуктовое событие, которое нельзя вывести из клика:
 * применён фильтр, выбраны метрики, включено сравнение и т.п.
 */
export const trackEvent = (
    elementId: string,
    payload?: Record<string, unknown>,
    eventType: AnalyticsEventType = 'action'
): void => {
    push({
        event_type: eventType,
        element_id: elementId,
        element_role: 'action',
        element_text: null,
        element_path: null,
        hit: generateHit(),
        payload: payload || null,
        ts: Date.now(),
        ...currentPage(),
    });
};

/** Переход между страницами ЛК */
export const trackNavigation = (from: string, to: string): void => {
    push({
        event_type: 'navigate',
        element_id: 'page-view',
        element_role: 'page',
        element_text: null,
        element_path: null,
        hit: generateHit(),
        payload: { from, to },
        ts: Date.now(),
        ...currentPage(),
    });
};

// ─── Автоматический сбор ────────────────────────────────────────────────────

const handleClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement | null;
    if (!target || !isAuthorized()) return;

    push({
        event_type: 'click',
        element_id: getElementId(target),
        element_role: getElementRole(target),
        element_text: getElementText(target),
        element_path: getElementPath(target),
        hit: generateHit(),
        payload: null,
        ts: Date.now(),
        ...currentPage(),
    });
};

/**
 * Изменение полей. Значения НЕ пишем — только факт изменения и имя поля:
 * в формах есть пароли, email и данные школ.
 */
const handleChange = (event: Event): void => {
    const target = event.target as HTMLInputElement | null;
    if (!target || !isAuthorized()) return;

    const tag = target.tagName?.toLowerCase();
    if (tag !== 'input' && tag !== 'select' && tag !== 'textarea') return;

    const type = (target.type || '').toLowerCase();
    if (type === 'password') return;

    const isToggle = type === 'checkbox' || type === 'radio';

    push({
        event_type: 'change',
        element_id: getElementId(target),
        element_role: isToggle ? type : 'input',
        element_text: getElementText(target),
        element_path: getElementPath(target),
        hit: generateHit(),
        payload: {
            field: target.name || target.getAttribute('data-analytics-field') || null,
            input_type: type || null,
            ...(isToggle ? { checked: target.checked } : {}),
        },
        ts: Date.now(),
        ...currentPage(),
    });
};

const handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') flush(true);
};

export const bindAnalytics = (): void => {
    if (bound) return;
    bound = true;

    // capture: true — событие фиксируется, даже если обработчик компонента
    // остановит всплытие (в antd это делают дропдауны и модалки)
    document.addEventListener('click', handleClick, true);
    document.addEventListener('change', handleChange, true);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', () => flush(true));

    flushTimer = setInterval(() => flush(), FLUSH_INTERVAL);
};

export const unbindAnalytics = (): void => {
    if (!bound) return;
    bound = false;

    document.removeEventListener('click', handleClick, true);
    document.removeEventListener('change', handleChange, true);
    document.removeEventListener('visibilitychange', handleVisibilityChange);

    if (flushTimer !== null) {
        clearInterval(flushTimer);
        flushTimer = null;
    }

    flush(true);
};
